import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Paper,
  Divider,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Card,
  CardContent,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Stack,
  Slider,
  Switch,
  FormControlLabel,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  ArrowBack as ArrowBackIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  Settings as SettingsIcon,
  ViewModule as SectionIcon,
  Palette as PaletteIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  Web as WebIcon,
} from '@mui/icons-material';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../../../contexts/AuthContext';
import {
  createTemplate,
  updateTemplate,
  getTemplate,
  CMSTemplate,
  CMSSection,
  TemplateLayoutSettings,
  SectionLayoutOptions,
} from '../../../../firebase/services/cmsService';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`template-tabpanel-${index}`}
      aria-labelledby={`template-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const TemplateEditor: React.FC = () => {
  const { userProfile } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEditing = Boolean(id);

  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [templateData, setTemplateData] = useState<Partial<CMSTemplate>>({
    name: '',
    description: '',
    category: 'custom',
    isSystem: false,
    sections: [],
    layoutSettings: {
      containerMaxWidth: 'lg',
      spacing: 4,
      backgroundColor: '#ffffff',
      padding: { top: 2, bottom: 2, left: 2, right: 2 },
      headerSettings: { showHeader: true },
      footerSettings: { showFooter: true },
    },
  });

  const [editingSection, setEditingSection] = useState<CMSSection | null>(null);
  const [sectionDialogOpen, setSectionDialogOpen] = useState(false);
  const [layoutDialogOpen, setLayoutDialogOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);

  useEffect(() => {
    if (isEditing && id) {
      loadTemplate(id);
    }
  }, [id, isEditing]);

  const loadTemplate = async (templateId: string) => {
    try {
      setLoading(true);
      setError(null);

      const template = await getTemplate(templateId);
      if (template) {
        setTemplateData(template);
      } else {
        setError('Template not found');
        navigate('/admin/business/online/templates');
      }
    } catch (err: any) {
      console.error('Error loading template:', err);
      setError(err.message || 'Failed to load template');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field: keyof CMSTemplate, value: any) => {
    setTemplateData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleLayoutSettingChange = (field: keyof TemplateLayoutSettings, value: any) => {
    // Convert "full" string to false for containerMaxWidth
    const processedValue = field === 'containerMaxWidth' && value === 'full' ? false : value;
    
    setTemplateData(prev => ({
      ...prev,
      layoutSettings: {
        ...prev.layoutSettings!,
        [field]: processedValue,
      },
    }));
  };

  const handleSectionEdit = (section: CMSSection) => {
    setEditingSection({ ...section });
    setSectionDialogOpen(true);
  };

  const handleSectionSave = () => {
    if (!editingSection) return;

    setTemplateData(prev => ({
      ...prev,
      sections: prev.sections?.map(section =>
        section.id === editingSection.id ? editingSection : section
      ) || [],
    }));

    setSectionDialogOpen(false);
    setEditingSection(null);
  };

  const handleSectionDelete = (sectionId: string) => {
    setTemplateData(prev => ({
      ...prev,
      sections: prev.sections?.filter(section => section.id !== sectionId) || [],
    }));
  };

  const handleSectionToggleVisibility = (sectionId: string) => {
    setTemplateData(prev => ({
      ...prev,
      sections: prev.sections?.map(section =>
        section.id === sectionId
          ? { ...section, isVisible: !section.isVisible }
          : section
      ) || [],
    }));
  };

  const handleAddSection = (type: CMSSection['type']) => {
    const newSection: CMSSection = {
      id: `section_${Date.now()}`,
      type,
      title: getSectionTypeLabel(type),
      content: getSectionDefaultContent(type),
      settings: getSectionDefaultSettings(type),
      order: (templateData.sections?.length || 0) + 1,
      isVisible: true,
    };

    setTemplateData(prev => ({
      ...prev,
      sections: [...(prev.sections || []), newSection],
    }));
  };

  const handleMoveSection = (sectionId: string, direction: 'up' | 'down') => {
    const sections = Array.from(templateData.sections || []);
    const currentIndex = sections.findIndex(s => s.id === sectionId);
    
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    
    if (newIndex < 0 || newIndex >= sections.length) return;
    
    // Swap the sections
    [sections[currentIndex], sections[newIndex]] = [sections[newIndex], sections[currentIndex]];
    
    // Update order values
    const reorderedSections = sections.map((section, index) => ({
      ...section,
      order: index + 1,
    }));

    setTemplateData(prev => ({
      ...prev,
      sections: reorderedSections,
    }));
  };

  const handleSave = async () => {
    if (!userProfile?.uid) return;

    try {
      setSaving(true);
      setError(null);

      const templatePayload = {
        ...templateData,
        updatedBy: userProfile.uid,
      };

      if (isEditing && id) {
        await updateTemplate(id, templatePayload);
        setSuccess('Template updated successfully');
      } else {
        const templateId = await createTemplate({
          ...templatePayload,
          createdBy: userProfile.uid,
        } as Omit<CMSTemplate, 'id' | 'createdAt' | 'updatedAt'>);
        setSuccess('Template created successfully');
        navigate(`/admin/business/online/templates/${templateId}/edit`);
      }
    } catch (err: any) {
      console.error('Error saving template:', err);
      setError(err.message || 'Failed to save template');
    } finally {
      setSaving(false);
    }
  };

  const getSectionTypeLabel = (type: CMSSection['type']): string => {
    const labels: Record<CMSSection['type'], string> = {
      hero: 'Hero Section',
      text: 'Text Section',
      image_text: 'Image & Text',
      gallery: 'Image Gallery',
      services: 'Services Grid',
      team: 'Team Section',
      timeline: 'Timeline',
      contact_info: 'Contact Info',
      cta: 'Call to Action',
      custom: 'Custom Section',
    };
    return labels[type] || 'Unknown Section';
  };

  const getSectionDefaultContent = (type: CMSSection['type']): string => {
    const content: Record<CMSSection['type'], string> = {
      hero: '<h1>Hero Title</h1><p>Hero description goes here</p>',
      text: '<p>Enter your content here...</p>',
      image_text: '<h2>Section Title</h2><p>Section description...</p>',
      gallery: '',
      services: '',
      team: '',
      timeline: '',
      contact_info: '',
      cta: '<h2>Ready to get started?</h2><p>Call to action description</p>',
      custom: '<p>Custom content...</p>',
    };
    return content[type] || '';
  };

  const getSectionDefaultSettings = (type: CMSSection['type']): Record<string, any> => {
    const settings: Record<CMSSection['type'], Record<string, any>> = {
      hero: {
        backgroundImage: '',
        overlayOpacity: 0.4,
        height: '400px',
        textAlign: 'center',
      },
      text: {},
      image_text: {
        imagePosition: 'left',
        imageUrl: '',
      },
      gallery: {
        columns: 3,
        spacing: 2,
      },
      services: {
        columns: 3,
        showIcons: true,
      },
      team: {
        columns: 3,
        showSocial: true,
      },
      timeline: {
        orientation: 'vertical',
      },
      contact_info: {
        showMap: true,
        showHours: true,
        showForm: true,
      },
      cta: {
        backgroundColor: '#007bff',
        textColor: 'white',
        buttonText: 'Get Started',
        buttonLink: '#',
      },
      custom: {},
    };
    return settings[type] || {};
  };

  const getSectionIcon = (type: CMSSection['type']) => {
    const icons: Record<CMSSection['type'], React.ReactNode> = {
      hero: <WebIcon />,
      text: <TextIcon />,
      image_text: <ImageIcon />,
      gallery: <ImageIcon />,
      services: <SectionIcon />,
      team: <SectionIcon />,
      timeline: <SectionIcon />,
      contact_info: <SectionIcon />,
      cta: <SectionIcon />,
      custom: <SettingsIcon />,
    };
    return icons[type] || <SectionIcon />;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton component={Link} to="/admin/business/online/templates" sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
              {isEditing ? 'Edit Template' : 'Create Template'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Design a reusable page template with custom sections and layout
            </Typography>
          </Box>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<PreviewIcon />}
            onClick={() => setPreviewMode(!previewMode)}
          >
            {previewMode ? 'Edit' : 'Preview'}
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={handleSave}
            disabled={saving || !templateData.name?.trim()}
          >
            {saving ? <CircularProgress size={20} /> : (isEditing ? 'Update' : 'Create')}
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {previewMode ? (
        <TemplatePreview template={templateData as CMSTemplate} />
      ) : (
        <Grid container spacing={3}>
          {/* Left Panel - Template Settings */}
          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                Template Settings
              </Typography>
              
              <TextField
                fullWidth
                label="Template Name"
                value={templateData.name || ''}
                onChange={(e) => handleInputChange('name', e.target.value)}
                margin="normal"
                required
              />
              
              <TextField
                fullWidth
                label="Description"
                value={templateData.description || ''}
                onChange={(e) => handleInputChange('description', e.target.value)}
                margin="normal"
                multiline
                rows={2}
              />
              
              <FormControl fullWidth margin="normal">
                <InputLabel>Category</InputLabel>
                <Select
                  value={templateData.category || 'custom'}
                  onChange={(e) => handleInputChange('category', e.target.value)}
                  label="Category"
                >
                  <MenuItem value="custom">Custom</MenuItem>
                  <MenuItem value="built-in">Built-in</MenuItem>
                </Select>
              </FormControl>
            </Paper>

            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Layout Settings
                </Typography>
                <IconButton onClick={() => setLayoutDialogOpen(true)}>
                  <SettingsIcon />
                </IconButton>
              </Box>

              <FormControl fullWidth margin="normal">
                <InputLabel>Container Width</InputLabel>
                <Select
                  value={templateData.layoutSettings?.containerMaxWidth === false ? 'full' : (templateData.layoutSettings?.containerMaxWidth || 'lg')}
                  onChange={(e) => handleLayoutSettingChange('containerMaxWidth', e.target.value)}
                  label="Container Width"
                >
                  <MenuItem value="xs">Extra Small</MenuItem>
                  <MenuItem value="sm">Small</MenuItem>
                  <MenuItem value="md">Medium</MenuItem>
                  <MenuItem value="lg">Large</MenuItem>
                  <MenuItem value="xl">Extra Large</MenuItem>
                  <MenuItem value="full">Full Width</MenuItem>
                </Select>
              </FormControl>

              <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
                Section Spacing: {templateData.layoutSettings?.spacing || 4}
              </Typography>
              <Slider
                value={templateData.layoutSettings?.spacing || 4}
                onChange={(_, value) => handleLayoutSettingChange('spacing', value)}
                min={0}
                max={8}
                step={1}
                marks
                valueLabelDisplay="auto"
              />
            </Paper>

            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Add Sections
              </Typography>
              <Grid container spacing={1}>
                {(['hero', 'text', 'image_text', 'gallery', 'services', 'team', 'timeline', 'contact_info', 'cta'] as CMSSection['type'][]).map((type) => (
                  <Grid item xs={6} key={type}>
                    <Button
                      variant="outlined"
                      fullWidth
                      size="small"
                      startIcon={getSectionIcon(type)}
                      onClick={() => handleAddSection(type)}
                      sx={{ mb: 1 }}
                    >
                      {getSectionTypeLabel(type)}
                    </Button>
                  </Grid>
                ))}
              </Grid>
            </Paper>
          </Grid>

          {/* Right Panel - Sections */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Template Sections
              </Typography>

              {!templateData.sections || templateData.sections.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <SectionIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="h6" color="text.secondary" gutterBottom>
                    No sections added yet
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Add sections from the left panel to build your template
                  </Typography>
                </Box>
              ) : (
                <div>
                  {templateData.sections
                    ?.sort((a, b) => a.order - b.order)
                    .map((section, index) => (
                      <Card
                        key={section.id}
                        sx={{
                          mb: 2,
                          opacity: section.isVisible ? 1 : 0.6,
                        }}
                      >
                        <CardContent>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            {/* Reorder buttons */}
                            <Box sx={{ display: 'flex', flexDirection: 'column', mr: 1 }}>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveSection(section.id, 'up')}
                                disabled={index === 0}
                                sx={{ p: 0.5 }}
                              >
                                <ArrowUpIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleMoveSection(section.id, 'down')}
                                disabled={index === (templateData.sections?.length || 0) - 1}
                                sx={{ p: 0.5 }}
                              >
                                <ArrowDownIcon fontSize="small" />
                              </IconButton>
                            </Box>
                            
                            {getSectionIcon(section.type)}
                            <Typography variant="h6" sx={{ ml: 1, flexGrow: 1 }}>
                              {section.title || getSectionTypeLabel(section.type)}
                            </Typography>
                            <Chip
                              label={section.type}
                              size="small"
                              variant="outlined"
                              sx={{ mr: 1 }}
                            />
                            <IconButton
                              size="small"
                              onClick={() => handleSectionToggleVisibility(section.id)}
                            >
                              {section.isVisible ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleSectionEdit(section)}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleSectionDelete(section.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Box>
                          {section.content && (
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{
                                display: '-webkit-box',
                                WebkitBoxOrient: 'vertical',
                                WebkitLineClamp: 2,
                                overflow: 'hidden',
                              }}
                              dangerouslySetInnerHTML={{ __html: section.content }}
                            />
                          )}
                        </CardContent>
                      </Card>
                    ))}
                </div>
              )}
            </Paper>
          </Grid>
        </Grid>
      )}

      {/* Section Edit Dialog */}
      <SectionEditDialog
        open={sectionDialogOpen}
        section={editingSection}
        onClose={() => setSectionDialogOpen(false)}
        onSave={handleSectionSave}
        onChange={setEditingSection}
      />

      {/* Layout Settings Dialog */}
      <LayoutSettingsDialog
        open={layoutDialogOpen}
        settings={templateData.layoutSettings || {
          containerMaxWidth: 'lg',
          spacing: 4,
          backgroundColor: '#ffffff',
          padding: { top: 2, bottom: 2, left: 2, right: 2 },
          headerSettings: { showHeader: true },
          footerSettings: { showFooter: true },
        }}
        onClose={() => setLayoutDialogOpen(false)}
        onChange={(settings) => setTemplateData(prev => ({ ...prev, layoutSettings: settings }))}
      />
    </Box>
  );
};

// Section Edit Dialog Component
interface SectionEditDialogProps {
  open: boolean;
  section: CMSSection | null;
  onClose: () => void;
  onSave: () => void;
  onChange: (section: CMSSection | null) => void;
}

const SectionEditDialog: React.FC<SectionEditDialogProps> = ({
  open,
  section,
  onClose,
  onSave,
  onChange,
}) => {
  if (!section) return null;

  const handleChange = (field: keyof CMSSection, value: any) => {
    onChange({
      ...section,
      [field]: value,
    });
  };

  const handleSettingChange = (key: string, value: any) => {
    onChange({
      ...section,
      settings: {
        ...section.settings,
        [key]: value,
      },
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Edit Section - {section.title}</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Section Title"
          value={section.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          margin="normal"
        />
        
        <TextField
          fullWidth
          label="Content"
          value={section.content || ''}
          onChange={(e) => handleChange('content', e.target.value)}
          margin="normal"
          multiline
          rows={4}
        />

        {/* Section-specific settings */}
        {section.type === 'hero' && (
          <>
            <TextField
              fullWidth
              label="Background Image URL"
              value={section.settings.backgroundImage || ''}
              onChange={(e) => handleSettingChange('backgroundImage', e.target.value)}
              margin="normal"
            />
            <Typography variant="body2" sx={{ mt: 2, mb: 1 }}>
              Overlay Opacity: {section.settings.overlayOpacity || 0.4}
            </Typography>
            <Slider
              value={section.settings.overlayOpacity || 0.4}
              onChange={(_, value) => handleSettingChange('overlayOpacity', value)}
              min={0}
              max={1}
              step={0.1}
              valueLabelDisplay="auto"
            />
          </>
        )}

        {section.type === 'image_text' && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Image Position</InputLabel>
            <Select
              value={section.settings.imagePosition || 'left'}
              onChange={(e) => handleSettingChange('imagePosition', e.target.value)}
              label="Image Position"
            >
              <MenuItem value="left">Left</MenuItem>
              <MenuItem value="right">Right</MenuItem>
              <MenuItem value="top">Top</MenuItem>
              <MenuItem value="bottom">Bottom</MenuItem>
            </Select>
          </FormControl>
        )}

        {(section.type === 'services' || section.type === 'team' || section.type === 'gallery') && (
          <TextField
            fullWidth
            label="Number of Columns"
            type="number"
            value={section.settings.columns || 3}
            onChange={(e) => handleSettingChange('columns', parseInt(e.target.value))}
            margin="normal"
            inputProps={{ min: 1, max: 6 }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={onSave} variant="contained">
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Layout Settings Dialog Component
interface LayoutSettingsDialogProps {
  open: boolean;
  settings: TemplateLayoutSettings;
  onClose: () => void;
  onChange: (settings: TemplateLayoutSettings) => void;
}

const LayoutSettingsDialog: React.FC<LayoutSettingsDialogProps> = ({
  open,
  settings,
  onClose,
  onChange,
}) => {
  const handleChange = (field: keyof TemplateLayoutSettings, value: any) => {
    onChange({
      ...settings,
      [field]: value,
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Layout Settings</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          label="Background Color"
          value={settings.backgroundColor || '#ffffff'}
          onChange={(e) => handleChange('backgroundColor', e.target.value)}
          margin="normal"
        />

        <Typography variant="h6" sx={{ mt: 3, mb: 2 }}>
          Padding
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Top"
              type="number"
              value={settings.padding?.top || 2}
              onChange={(e) => handleChange('padding', { ...settings.padding, top: parseInt(e.target.value) })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Bottom"
              type="number"
              value={settings.padding?.bottom || 2}
              onChange={(e) => handleChange('padding', { ...settings.padding, bottom: parseInt(e.target.value) })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Left"
              type="number"
              value={settings.padding?.left || 2}
              onChange={(e) => handleChange('padding', { ...settings.padding, left: parseInt(e.target.value) })}
            />
          </Grid>
          <Grid item xs={6}>
            <TextField
              fullWidth
              label="Right"
              type="number"
              value={settings.padding?.right || 2}
              onChange={(e) => handleChange('padding', { ...settings.padding, right: parseInt(e.target.value) })}
            />
          </Grid>
        </Grid>

        <FormControlLabel
          control={
            <Switch
              checked={settings.headerSettings?.showHeader || false}
              onChange={(e) => handleChange('headerSettings', { ...settings.headerSettings, showHeader: e.target.checked })}
            />
          }
          label="Show Header"
          sx={{ mt: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={settings.footerSettings?.showFooter || false}
              onChange={(e) => handleChange('footerSettings', { ...settings.footerSettings, showFooter: e.target.checked })}
            />
          }
          label="Show Footer"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// Template Preview Component
interface TemplatePreviewProps {
  template: CMSTemplate;
}

const TemplatePreview: React.FC<TemplatePreviewProps> = ({ template }) => {
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Template Preview: {template.name}
      </Typography>
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          backgroundColor: template.layoutSettings?.backgroundColor || '#ffffff',
        }}
      >
        {template.sections
          ?.sort((a, b) => a.order - b.order)
          .filter(section => section.isVisible)
          .map((section) => (
            <Card key={section.id} sx={{ mb: template.layoutSettings?.spacing || 4 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {section.title}
                </Typography>
                {section.content && (
                  <Typography
                    component="div"
                    dangerouslySetInnerHTML={{ __html: section.content }}
                  />
                )}
              </CardContent>
            </Card>
          ))}
      </Box>
    </Paper>
  );
};

export default TemplateEditor; 